/* eslint-disable react/jsx-props-no-spreading */

import ActionTable from 'components/table/actionTable';
import PaginateTable from 'components/table/paginate';
import { permissionKeys, ProgramStatus, routePaths, sortByTypeType } from 'utils/constants';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import NoResult from 'components/commonComponent/noResult';
import SpinnerComponent from 'components/spinner';
import Table from 'components/table/table';
import { changeProgramStatus, getPatientPrograms } from 'api/programApi';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../../styles/styles.scss'
import './patientPrograms.scss'
import Sort from 'components/table/sort';
import { checkPermission, messageErrors } from 'utils/utils';
import { useSelector } from 'react-redux';
import { orgSelector, userSelector } from 'redux/selectors';
import moment from 'moment';
import Avatar from '../../components/GroupAvatar/Avatar';
import ListEmpty from 'components/empty/ListEmpty';
import Svg from 'components/svg';
import { useNavigate } from 'react-router';

const PatientPrograms = (props: any) => {


    const [t] = useTranslation();
    const { userInfo } = useSelector(userSelector);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalEntities, setTotalEntities] = useState(0);
    const [programs, setPrograms] = useState([]);
    const params: any = useParams();
    const [sortBy, setSortBy] = useState('');
    const [sortByType, setSortByType] = useState('');
    const [allPrograms, setAllPrograms] = useState(0)
    const { WRITE_PROGRAM } = permissionKeys;
    const { organizationId } = useSelector(orgSelector);

    const isWriteProgram = checkPermission(userInfo, props.type, [WRITE_PROGRAM], organizationId);
    const patientId = params?.id ?? userInfo?.id;

    const { data: allProgramData } = useQuery(
        ['getAllPatientPrograms', patientId],
        () =>
            getPatientPrograms({
                page: 0,
                limit: 0,
                patientId: patientId,
            }),
        {
            onError: (error) => {
                setTotalEntities(0)
                setPrograms([])
                setAllPrograms(0)
                const message: string = messageErrors(error, t);
                toast.error(message);
            },
            staleTime: Infinity,
        },
    );

    const { data, isLoading } = useQuery(
        ['getPatientPrograms', currentPage, searchValue, patientId, sortBy, sortByType],
        () =>
            getPatientPrograms({
                page: currentPage,
                limit: 10,
                searchQuery: searchValue,
                patientId: patientId,
                sortBy,
                sortByType
            }),
        {
            onSuccess: (data: any) => {
                setTotalEntities(data.totalEntities);
            },
            onError: (error) => {
                setTotalEntities(0)
                setPrograms([])
                setAllPrograms(0)
                const message: string = messageErrors(error, t);
                toast.error(message);
            },
            staleTime: Infinity,
        },
    );


    const handleHeaderSort = (field: string) => {
        setSortBy(field);
        let type = '';
        if (field !== sortBy) {
            type = sortByTypeType.ASC
        } else if (sortByType === sortByTypeType.ASC) {
            type = sortByTypeType.DESC;
        } else {
            setSortBy(type);
        }
        setSortByType(type)
    }

    const handleChangeStatus = (data: any) => {
        return changeProgramStatus(data)
    };

    const mutationUpdateProgramStatus = useMutation('changr-status', {
        mutationFn: handleChangeStatus,
        onSuccess: () => {
            queryClient.invalidateQueries('getPatientPrograms', { refetchActive: true }, { cancelRefetch: true });
        },
        onError: async (error: any) => {
            const message: string = `${error.response.data.errors?.[0].detail}`;
            toast.error(message);
        },
    });
    const handleSearch = (value: string) => {
        setSearchValue(value);
        setCurrentPage(0);
    };

    const handleViewDetail = (id: string) => {
        navigate(`${routePaths.PATIENT_PROGRAM}/${id}`);
    }

    useEffect(() => {
        if (data !== undefined) {
            setPrograms(data?.data?.entities);
            setTotalEntities(data?.data.totalEntities);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        setTotalEntities(data?.data.totalEntities);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (allProgramData) {
            setAllPrograms(allProgramData?.data?.entities?.length)
        }
    }, [allProgramData])


    if (!isLoading && isEmpty(searchValue) && totalEntities === 0 && allPrograms === 0 && isWriteProgram) {
        return (
            <div className="w-full py-2 px-6 ">
                <ListEmpty
                    buttonName={t('addProgram')}
                    note={t('noteProgramEmpty')}
                    title={t('titleProgramEmpty')}
                    icon={<Svg name='program-empty' className='h-[84px] w-auto' />}
                />
            </div>
        );
    }

    return (
        <div className='patient-programs h-full flex flex-col'>
            {allPrograms > 0 && (
                <div className="md:mx-6 md:mt-2 mx-4 justify-between grow flex flex-col">
                    <div className='py-5'>
                        <ActionTable placeholderSearch={t('Search')} buttonName={t('addProgram')} handleSearch={handleSearch}
                        />
                    </div>
                    {totalEntities === 0 && !isLoading && <div className=" mb-2 grow"><NoResult /> </div>}
                    {totalEntities > 0 && programs.length > 0 && (
                        <>
                            <div className=" mb-2 grow">
                                <Table className="text-black">
                                    <Table.Head className="text-black">
                                        <Table.HeadCell className="cursor-pointer" onClick={() => handleHeaderSort('name')}>
                                            <div className="flex items-center">
                                                {t('patientPrograms.table.programName')}
                                                <Sort check={sortBy === 'name'} sortByType={sortByType} />
                                            </div>
                                        </Table.HeadCell>
                                        <Table.HeadCell className="hidden-mobile-tablet">
                                            <div className="flex items-center">{t('patientPrograms.table.type')}</div>
                                        </Table.HeadCell>
                                        <Table.HeadCell className="hidden-mobile">
                                            <div className="flex items-center">{t('patientPrograms.table.lastUpdatedDate')}</div>
                                        </Table.HeadCell>
                                        <Table.HeadCell >
                                            <div className="flex items-center">{t('status')}</div>
                                        </Table.HeadCell>
                                        <Table.HeadCell className="hidden-mobile">
                                            <div className="flex items-center">{t('patientPrograms.table.therapist')}</div>
                                        </Table.HeadCell>
                                    </Table.Head>
                                    <Table.Body>
                                        {programs.map((item: any) => (
                                            <Table.Row onClick={() => handleViewDetail(item.id)} key={`row-${item.id}`} className="bg-white text-base hover:border-b text-on-primary-container cursor-pointer">
                                                <Table.Cell className="font-medium items-center text-black h-full"
                                                >
                                                    {item.name}
                                                </Table.Cell>
                                                <Table.Cell className="text-sm hidden-mobile-tablet py-2.5">
                                                    {item.type}
                                                </Table.Cell>
                                                <Table.Cell className="text-sm hidden-mobile py-2.5">
                                                    {moment(item?.modified).format('MMMM DD, YYYY')}
                                                </Table.Cell>
                                                <Table.Cell className="text-sm  py-2.5">
                                                    <div className="flex flex-row items-center">
                                                        {item.status === ProgramStatus.ACTIVE && <span className="w-3 h-3 bg-green-400 rounded-lg mr-2 " />}
                                                        {item.status === ProgramStatus.INACTIVE && <span className="w-3 h-3 bg-red-500 rounded-lg mr-2 " />}
                                                        {item.status === ProgramStatus.DRAFT && <span className="w-3 h-3 bg-purple-400 rounded-lg mr-2 " />}
                                                        {item.status}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell className="flex items-center hidden-mobile py-2.5">
                                                    {item?.patient?.organizations[0]?.therapist && <div className="flex items-center gap-2 font-semibold">
                                                        <Avatar item={item?.patient?.organizations[0]?.therapist} /> {`${item?.patient?.organizations[0]?.therapist?.firstName} ${item?.patient?.organizations[0]?.therapist?.lastName}`}
                                                    </div>}
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </div>
                            <PaginateTable setCurrentPage={setCurrentPage} currentPage={currentPage} totalEntities={totalEntities} pageCount={5} />
                        </>
                    )}
                </div>
            )}
          {(isLoading || mutationUpdateProgramStatus.isLoading) && <SpinnerComponent />}
        </div >
    )
}
export default PatientPrograms
